import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  fetchTrackWithTuxData,
  nextLogId,
  nextLeaveId,
  nextAnnouncementId,
  nextHolidayId,
  nextHolidayListId,
  nextClientId,
  nextRoleId,
  nextCriteriaId,
  nextApplicantId,
  nextEmployeeUuid,
} from '../utils/sheetsApi';
import {
  postCheckInOut,
  postEditNote,
  postFileLeave,
  postDecideLeave,
  postAdjustBalance,
  postCancelLeave,
  postCreateAnnouncement,
  postUpdateAnnouncement,
  postDeleteAnnouncement,
  postCreateHoliday,
  postUpdateHoliday,
  postDeleteHoliday,
  postCreateHolidayList,
  postUpdateHolidayList,
  postDeleteHolidayList,
  postCreateClient,
  postUpdateClient,
  postDeleteClient,
  postAddClientStaff,
  postRemoveClientStaff,
  postCreateRole,
  postUpdateRole,
  postDeleteRole,
  postUpdateRoleCriteria,
  postCreateApplicant,
  postUpdateApplicant,
  postDeleteApplicant,
  postCreateEmployee,
  postUpdateEmployee,
  postDeactivateEmployee,
  postReactivateEmployee,
} from '../utils/appsScript';
import { decodeJwtPayload } from '../utils/jwt';
import { simulateNetworkDelay } from '../utils/async';
import { useLoading } from './LoadingContext';
import { useToast } from './ToastContext';

const AppContext = createContext(null);
const SESSION_KEY = 'trackwithtux_uuid';

export function AppProvider({ children }) {
  const { withLoading } = useLoading();
  const { showError } = useToast();

  const [users, setUsers] = useState([]);
  const [timelogs, setTimelogs] = useState([]);
  const [tzOffsets, setTzOffsets] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [holidayLists, setHolidayLists] = useState([]);
  const [holidayListItems, setHolidayListItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientStaff, setClientStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleCriteria, setRoleCriteria] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [applicantAnswers, setApplicantAnswers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataError, setDataError] = useState(null);

  // The Google ID token from sign-in, kept in memory only (never persisted —
  // it's short-lived and there's no reason to put it in localStorage). A
  // hard refresh always clears it, so the next check-in/out will ask for a
  // fresh sign-in before writing, same as a naturally expired token would.
  const [idToken, setIdTokenRaw] = useState(null);
  const [idTokenExpiry, setIdTokenExpiry] = useState(null);

  const setIdToken = (token) => {
    if (!token) {
      setIdTokenRaw(null);
      setIdTokenExpiry(null);
      return;
    }
    const { exp } = decodeJwtPayload(token);
    setIdTokenRaw(token);
    setIdTokenExpiry(exp || null);
  };

  const isIdTokenValid = Boolean(idToken && idTokenExpiry && Date.now() < idTokenExpiry * 1000);

  // Re-fetches Users/Timelogs/TZ Offsets from the live sheet. Note: this
  // overwrites Timelogs with whatever's actually on the sheet.
  const loadData = useCallback(
    async (isInitialLoad) => {
      const sheetId = process.env.REACT_APP_SHEETS_ID;
      const apiKey = process.env.REACT_APP_SHEETS_API_KEY;

      if (!sheetId || !apiKey) {
        const detail = 'Sheets not configured — missing REACT_APP_SHEETS_ID or REACT_APP_SHEETS_API_KEY.';
        setDataError(detail);
        showError(detail);
        setDataLoaded(true);
        return;
      }

      try {
        const data = await fetchTrackWithTuxData(sheetId, apiKey);
        setUsers(data.users);
        setTimelogs(data.timelogs);
        setTzOffsets(data.tzOffsets);
        setLeaves(data.leaves);
        setLeaveBalances(data.leaveBalances);
        setAnnouncements(data.announcements);
        setHolidays(data.holidays);
        setHolidayLists(data.holidayLists);
        setHolidayListItems(data.holidayListItems);
        setClients(data.clients);
        setClientStaff(data.clientStaff);
        setRoles(data.roles);
        setRoleCriteria(data.roleCriteria);
        setApplicants(data.applicants);
        setApplicantAnswers(data.applicantAnswers);
        setDataError(null);

        if (isInitialLoad) {
          const savedUuid = localStorage.getItem(SESSION_KEY);
          if (savedUuid) {
            const restored = data.users.find((u) => u.uuid === savedUuid);
            if (restored) setCurrentUser(restored);
          }
        } else {
          setCurrentUser((prev) => (prev ? data.users.find((u) => u.uuid === prev.uuid) || prev : prev));
        }

        return data;
      } catch (err) {
        const detail = err.detail || err.message;
        setDataError(detail);
        showError(`Couldn't load the team sheet — ${detail}`);
        return null;
      } finally {
        setDataLoaded(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    withLoading(() => loadData(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetch = () => withLoading(() => loadData(false));

  // Looks up a Gmail address against the Users list — stands in for the
  // Apps Script "look up user by email" endpoint (Handoff §5, step 4).
  const signInWithGmail = (gmail) => {
    const match = users.find((u) => u.gmail.toLowerCase() === gmail.trim().toLowerCase());
    if (match) {
      setCurrentUser(match);
      localStorage.setItem(SESSION_KEY, match.uuid);
    }
    return match || null;
  };

  const signOut = () => {
    setCurrentUser(null);
    setIdToken(null);
    localStorage.removeItem(SESSION_KEY);
  };

  // Writes to the sheet via the Apps Script endpoint (apps-script/Code.gs)
  // when it's configured; falls back to local-only state if it isn't set up
  // yet, same as before. Requires a still-valid ID token — callers should
  // check isIdTokenValid first and get a fresh one if not.
  //
  // The POST goes out as mode: 'no-cors' (see utils/appsScript.js) so its
  // response is opaque — we can't read success/failure or the log_id it
  // generated from it directly. Instead, right after posting, we re-fetch
  // the sheet and check whether the expected row actually shows up for this
  // person. That's the real confirmation, not the POST call itself.
  // `logtimeUtc`/`isManual` let a caller backdate the entry instead of
  // stamping "now" — supported end-to-end (Apps Script included), but no
  // current UI calls addLog with them. Every real caller (the live Check
  // In/Out button) omits them.
  const addLog = async ({ isIn, notes, logtimeUtc, isManual }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;
    const effectiveLogtimeUtc = isManual ? logtimeUtc : new Date().toISOString();

    if (!appsScriptUrl) {
      const entry = {
        log_id: nextLogId(timelogs),
        uuid: currentUser.uuid,
        isIn,
        logtime_utc: effectiveLogtimeUtc.replace(/\.\d{3}Z$/, 'Z'),
        notes: notes || '',
        is_manual: Boolean(isManual),
      };
      setTimelogs((prev) => [...prev, entry]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCheckInOut({
      appsScriptUrl,
      idToken,
      uuid: currentUser.uuid,
      isIn,
      notes: notes || '',
      logtimeUtc: isManual ? logtimeUtc : undefined,
      isManual,
    });

    // Small buffer before reading back — the Sheets API can lag a beat
    // behind an Apps Script write landing.
    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const freshLogsForUser = (fresh ? fresh.timelogs : []).filter((l) => l.uuid === currentUser.uuid);

    let wroteSuccessfully;
    if (isManual) {
      // A backfilled entry's timestamp is in the past by design and won't
      // necessarily be this person's most recent log — match on uuid/isIn
      // plus the specific timestamp instead of "latest and just happened".
      wroteSuccessfully = freshLogsForUser.some(
        (l) =>
          l.isIn === isIn &&
          Math.abs(new Date(l.logtime_utc).getTime() - new Date(logtimeUtc).getTime()) < 5000
      );
    } else {
      const latest = freshLogsForUser.reduce(
        (best, l) => (!best || new Date(l.logtime_utc) > new Date(best.logtime_utc) ? l : best),
        null
      );
      wroteSuccessfully =
        latest && latest.isIn === isIn && Date.now() - new Date(latest.logtime_utc).getTime() < 2 * 60 * 1000;
    }

    if (!wroteSuccessfully) {
      throw new Error("Couldn't confirm the write landed on the sheet — check the connection and try again.");
    }
  };

  // Edits the note on an existing log entry — self-service only, Apps
  // Script rejects this if logId doesn't actually belong to currentUser.
  // Never creates a new row, unlike addLog. Same "write, wait, refetch,
  // verify" pattern as addLog, since the POST response is opaque here too.
  const editNote = async ({ logId, notes }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setTimelogs((prev) =>
        prev.map((l) => (l.log_id === logId ? { ...l, notes, is_manual: l.is_manual, notes_edited: true } : l))
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postEditNote({ appsScriptUrl, idToken, uuid: currentUser.uuid, logId, notes });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.timelogs : []).find((l) => l.log_id === logId);

    if (!updated || updated.notes !== notes) {
      throw new Error("Couldn't confirm the note was saved — check the connection and try again.");
    }
  };

  // Files a new leave request for currentUser — always starts Pending, never
  // touches balances. isHalfDay only takes effect when dateFrom === dateTo
  // (Code.gs rejects it otherwise, same guard applied here for the local
  // fallback). Same "write, wait, refetch, verify" pattern as addLog.
  const fileLeave = async ({ leaveType, dateFrom, dateTo, isHalfDay, halfDayPeriod, reason }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;
    const halfDay = Boolean(isHalfDay) && dateFrom === dateTo;
    const period = halfDay ? halfDayPeriod : '';
    const days = halfDay ? 0.5 : Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000) + 1;

    if (!appsScriptUrl) {
      const entry = {
        leave_id: nextLeaveId(leaves),
        uuid: currentUser.uuid,
        leave_type: leaveType,
        date_from: dateFrom,
        date_to: dateTo,
        days,
        half_day_period: period,
        reason: reason || '',
        status: 'Pending',
        filed_at: new Date().toISOString(),
        decided_by: '',
        decided_at: '',
        decision_notes: '',
      };
      setLeaves((prev) => [...prev, entry]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postFileLeave({
      appsScriptUrl,
      idToken,
      uuid: currentUser.uuid,
      leaveType,
      dateFrom,
      dateTo,
      isHalfDay: halfDay,
      halfDayPeriod: period,
      reason: reason || '',
    });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.leaves : []).some(
      (l) =>
        l.uuid === currentUser.uuid &&
        l.date_from === dateFrom &&
        l.date_to === dateTo &&
        l.leave_type === leaveType &&
        l.status === 'Pending'
    );

    if (!landed) {
      throw new Error("Couldn't confirm the request was saved — check the connection and try again.");
    }
  };

  // Approves or disapproves a Pending leave — admin-only (Apps Script
  // enforces this; the UI only exposes it to admins in the first place).
  // Approving deducts `days` from the filer's balance — Apps Script rejects
  // the whole thing if that would go negative, so the local fallback mirrors
  // that same guard rather than silently allowing it.
  const decideLeave = async ({ leaveId, decision, decisionNotes }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const target = leaves.find((l) => l.leave_id === leaveId);
      if (!target || target.status !== 'Pending') return;

      if (decision === 'Approved') {
        const bal = leaveBalances.find((b) => b.uuid === target.uuid);
        const remaining = bal ? bal.credits_remaining : 0;
        if (target.days > remaining) {
          throw new Error(`This would exceed their remaining balance (${remaining} left, ${target.days} requested).`);
        }
        setLeaveBalances((prev) =>
          prev.map((b) =>
            b.uuid === target.uuid
              ? { ...b, credits_remaining: b.credits_remaining - target.days, updated_at: new Date().toISOString() }
              : b
          )
        );
      }

      setLeaves((prev) =>
        prev.map((l) =>
          l.leave_id === leaveId
            ? {
                ...l,
                status: decision,
                decided_by: currentUser.uuid,
                decided_at: new Date().toISOString(),
                decision_notes: decisionNotes || '',
              }
            : l
        )
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDecideLeave({ appsScriptUrl, idToken, uuid: currentUser.uuid, leaveId, decision, decisionNotes });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.leaves : []).find((l) => l.leave_id === leaveId);

    if (!updated || updated.status !== decision) {
      throw new Error("Couldn't confirm the decision was saved — check the connection and try again.");
    }
  };

  // Sets someone's credits_remaining directly — admin-only. Upsert, same as
  // the Apps Script side: creates their LeaveBalances row if it doesn't
  // exist yet.
  const adjustBalance = async ({ targetUuid, creditsTotal, creditsRemaining }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (creditsRemaining > creditsTotal) {
      throw new Error("Remaining can't be more than total.");
    }

    if (!appsScriptUrl) {
      setLeaveBalances((prev) => {
        const exists = prev.some((b) => b.uuid === targetUuid);
        const nowIso = new Date().toISOString();
        if (exists) {
          return prev.map((b) =>
            b.uuid === targetUuid
              ? { ...b, credits_total: creditsTotal, credits_remaining: creditsRemaining, updated_at: nowIso }
              : b
          );
        }
        return [
          ...prev,
          { uuid: targetUuid, credits_total: creditsTotal, credits_remaining: creditsRemaining, updated_at: nowIso },
        ];
      });
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postAdjustBalance({ appsScriptUrl, idToken, uuid: currentUser.uuid, targetUuid, creditsTotal, creditsRemaining });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.leaveBalances : []).find((b) => b.uuid === targetUuid);

    if (!updated || updated.credits_total !== creditsTotal || updated.credits_remaining !== creditsRemaining) {
      throw new Error("Couldn't confirm the balance was saved — check the connection and try again.");
    }
  };

  // Withdraws one's own still-Pending leave — self-service only, same as the
  // Apps Script side.
  const cancelLeave = async ({ leaveId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setLeaves((prev) =>
        prev.map((l) =>
          l.leave_id === leaveId && l.uuid === currentUser.uuid && l.status === 'Pending'
            ? { ...l, status: 'Cancelled' }
            : l
        )
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCancelLeave({ appsScriptUrl, idToken, uuid: currentUser.uuid, leaveId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.leaves : []).find((l) => l.leave_id === leaveId);

    if (!updated || updated.status !== 'Cancelled') {
      throw new Error("Couldn't confirm the cancellation was saved — check the connection and try again.");
    }
  };

  // Creates a new announcement — admin-only (Apps Script enforces this; the
  // UI only exposes creation to admins in the first place). `fields` is
  // already shaped by the caller (Announcements.js) to whichever of
  // endDate / month+day+leadDays / startDate+intervalDays+leadDays apply to
  // fields.recurrenceType — same division of labor as fileLeave, where the
  // screen builds the type-specific shape and this just passes it through.
  const createAnnouncement = async (fields) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const entry = {
        announcement_id: nextAnnouncementId(announcements),
        message: fields.message,
        recurrence_type: fields.recurrenceType,
        month: fields.month ?? '',
        day: fields.day ?? '',
        start_date: fields.startDate ?? '',
        end_date: fields.endDate ?? '',
        lead_days: fields.leadDays ?? 0,
        active: true,
        created_by: currentUser.uuid,
        created_at: new Date().toISOString(),
      };
      setAnnouncements((prev) => [...prev, entry]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateAnnouncement({ appsScriptUrl, idToken, uuid: currentUser.uuid, fields });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.announcements : []).some(
      (a) =>
        a.created_by === currentUser.uuid && a.message === fields.message && a.recurrence_type === fields.recurrenceType
    );

    if (!landed) {
      throw new Error("Couldn't confirm the announcement was saved — check the connection and try again.");
    }
  };

  // Overwrites an existing announcement's editable fields — admin-only.
  // `active: false` is also how a row gets deactivated without deleting it.
  const updateAnnouncement = async ({ announcementId, fields, active = true }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setAnnouncements((prev) =>
        prev.map((a) =>
          a.announcement_id === announcementId
            ? {
                ...a,
                message: fields.message,
                recurrence_type: fields.recurrenceType,
                month: fields.month ?? '',
                day: fields.day ?? '',
                start_date: fields.startDate ?? '',
                end_date: fields.endDate ?? '',
                lead_days: fields.leadDays ?? 0,
                active,
              }
            : a
        )
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateAnnouncement({ appsScriptUrl, idToken, uuid: currentUser.uuid, announcementId, fields, active });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.announcements : []).find((a) => a.announcement_id === announcementId);

    if (!updated || updated.message !== fields.message || updated.active !== active) {
      throw new Error("Couldn't confirm the announcement was saved — check the connection and try again.");
    }
  };

  // Permanently removes an announcement — admin-only.
  const deleteAnnouncement = async ({ announcementId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setAnnouncements((prev) => prev.filter((a) => a.announcement_id !== announcementId));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeleteAnnouncement({ appsScriptUrl, idToken, uuid: currentUser.uuid, announcementId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.announcements : []).some((a) => a.announcement_id === announcementId);

    if (stillThere) {
      throw new Error("Couldn't confirm the announcement was deleted — check the connection and try again.");
    }
  };

  // Creates a new holiday — admin-only. Month/day only, no year (holidays
  // repeat every year).
  const createHoliday = async ({ name, month, day }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const entry = {
        holiday_id: nextHolidayId(holidays),
        name,
        month,
        day,
        created_by: currentUser.uuid,
        created_at: new Date().toISOString(),
      };
      setHolidays((prev) => [...prev, entry]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateHoliday({ appsScriptUrl, idToken, uuid: currentUser.uuid, name, month, day });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.holidays : []).some(
      (h) => h.created_by === currentUser.uuid && h.name === name && Number(h.month) === Number(month) && Number(h.day) === Number(day)
    );

    if (!landed) {
      throw new Error("Couldn't confirm the holiday was saved — check the connection and try again.");
    }
  };

  // Overwrites an existing holiday's name/month/day — admin-only.
  const updateHoliday = async ({ holidayId, name, month, day }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setHolidays((prev) => prev.map((h) => (h.holiday_id === holidayId ? { ...h, name, month, day } : h)));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateHoliday({ appsScriptUrl, idToken, uuid: currentUser.uuid, holidayId, name, month, day });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.holidays : []).find((h) => h.holiday_id === holidayId);

    if (!updated || updated.name !== name || Number(updated.month) !== Number(month) || Number(updated.day) !== Number(day)) {
      throw new Error("Couldn't confirm the holiday was saved — check the connection and try again.");
    }
  };

  // Permanently removes a holiday — admin-only. Code.gs also cleans up its
  // HolidayListItems rows on its side; the local fallback mirrors that here.
  const deleteHoliday = async ({ holidayId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setHolidays((prev) => prev.filter((h) => h.holiday_id !== holidayId));
      setHolidayListItems((prev) => prev.filter((item) => item.holiday_id !== holidayId));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeleteHoliday({ appsScriptUrl, idToken, uuid: currentUser.uuid, holidayId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.holidays : []).some((h) => h.holiday_id === holidayId);

    if (stillThere) {
      throw new Error("Couldn't confirm the holiday was deleted — check the connection and try again.");
    }
  };

  // Creates a new holiday list plus its initial membership — admin-only.
  // holidayIds is the full initial set (can be empty).
  const createHolidayList = async ({ name, holidayIds }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const listId = nextHolidayListId(holidayLists);
      const entry = { list_id: listId, name, created_by: currentUser.uuid, created_at: new Date().toISOString() };
      setHolidayLists((prev) => [...prev, entry]);
      setHolidayListItems((prev) => [...prev, ...holidayIds.map((holidayId) => ({ list_id: listId, holiday_id: holidayId }))]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateHolidayList({ appsScriptUrl, idToken, uuid: currentUser.uuid, name, holidayIds });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.holidayLists : []).some((l) => l.created_by === currentUser.uuid && l.name === name);

    if (!landed) {
      throw new Error("Couldn't confirm the holiday list was saved — check the connection and try again.");
    }
  };

  // Overwrites a holiday list's name and fully replaces its membership —
  // admin-only. holidayIds is always the FULL desired membership, not a
  // delta — same full-state shape as adjustBalance/updateAnnouncement.
  const updateHolidayList = async ({ listId, name, holidayIds }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setHolidayLists((prev) => prev.map((l) => (l.list_id === listId ? { ...l, name } : l)));
      setHolidayListItems((prev) => [
        ...prev.filter((item) => item.list_id !== listId),
        ...holidayIds.map((holidayId) => ({ list_id: listId, holiday_id: holidayId })),
      ]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateHolidayList({ appsScriptUrl, idToken, uuid: currentUser.uuid, listId, name, holidayIds });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.holidayLists : []).find((l) => l.list_id === listId);
    const updatedItemIds = (fresh ? fresh.holidayListItems : [])
      .filter((item) => item.list_id === listId)
      .map((item) => item.holiday_id)
      .sort();
    const expectedIds = [...holidayIds].sort();
    const membershipMatches =
      updatedItemIds.length === expectedIds.length && updatedItemIds.every((id, i) => id === expectedIds[i]);

    if (!updated || updated.name !== name || !membershipMatches) {
      throw new Error("Couldn't confirm the holiday list was saved — check the connection and try again.");
    }
  };

  // Permanently removes a holiday list — admin-only. Code.gs also cleans up
  // its HolidayListItems rows on its side; the local fallback mirrors that.
  const deleteHolidayList = async ({ listId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setHolidayLists((prev) => prev.filter((l) => l.list_id !== listId));
      setHolidayListItems((prev) => prev.filter((item) => item.list_id !== listId));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeleteHolidayList({ appsScriptUrl, idToken, uuid: currentUser.uuid, listId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.holidayLists : []).some((l) => l.list_id === listId);

    if (stillThere) {
      throw new Error("Couldn't confirm the holiday list was deleted — check the connection and try again.");
    }
  };

  // Creates a new client — admin-only. contact* is the client's own point
  // of contact (a named person on their side), distinct from ClientStaff
  // (TUX's own staff assigned to the account).
  const createClient = async ({ name, description, industry, contactName, contactTitle, contactEmail, contactPhone }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const entry = {
        client_id: nextClientId(clients),
        name,
        description: description || '',
        industry: industry || '',
        contact_name: contactName || '',
        contact_title: contactTitle || '',
        contact_email: contactEmail || '',
        contact_phone: contactPhone || '',
        created_by: currentUser.uuid,
        created_at: new Date().toISOString(),
      };
      setClients((prev) => [...prev, entry]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateClient({
      appsScriptUrl,
      idToken,
      uuid: currentUser.uuid,
      name,
      description,
      industry,
      contactName,
      contactTitle,
      contactEmail,
      contactPhone,
    });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.clients : []).some((c) => c.created_by === currentUser.uuid && c.name === name);

    if (!landed) {
      throw new Error("Couldn't confirm the client was saved — check the connection and try again.");
    }
  };

  // Overwrites an existing client's name/description/industry/contact* —
  // admin-only.
  const updateClient = async ({
    clientId,
    name,
    description,
    industry,
    contactName,
    contactTitle,
    contactEmail,
    contactPhone,
  }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setClients((prev) =>
        prev.map((c) =>
          c.client_id === clientId
            ? {
                ...c,
                name,
                description: description || '',
                industry: industry || '',
                contact_name: contactName || '',
                contact_title: contactTitle || '',
                contact_email: contactEmail || '',
                contact_phone: contactPhone || '',
              }
            : c
        )
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateClient({
      appsScriptUrl,
      idToken,
      uuid: currentUser.uuid,
      clientId,
      name,
      description,
      industry,
      contactName,
      contactTitle,
      contactEmail,
      contactPhone,
    });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.clients : []).find((c) => c.client_id === clientId);

    if (!updated || updated.name !== name) {
      throw new Error("Couldn't confirm the client was saved — check the connection and try again.");
    }
  };

  // Permanently removes a client — admin-only. Code.gs also cleans up its
  // ClientStaff rows on its side; the local fallback mirrors that here.
  const deleteClient = async ({ clientId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setClients((prev) => prev.filter((c) => c.client_id !== clientId));
      setClientStaff((prev) => prev.filter((s) => s.client_id !== clientId));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeleteClient({ appsScriptUrl, idToken, uuid: currentUser.uuid, clientId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.clients : []).some((c) => c.client_id === clientId);

    if (stillThere) {
      throw new Error("Couldn't confirm the client was deleted — check the connection and try again.");
    }
  };

  // Adds (or, if already assigned, updates the role of) one staff member on
  // a client — admin-only. Upsert on (clientId, targetUuid), same as
  // Code.gs's handler — there's no separate "edit role" action, re-adding
  // someone just updates their role.
  const addClientStaff = async ({ clientId, targetUuid, role }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setClientStaff((prev) => {
        const existing = prev.find((s) => s.client_id === clientId && s.uuid === targetUuid);
        if (existing) {
          return prev.map((s) => (s === existing ? { ...s, role: role || '' } : s));
        }
        return [...prev, { client_id: clientId, uuid: targetUuid, role: role || '', created_at: new Date().toISOString() }];
      });
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postAddClientStaff({ appsScriptUrl, idToken, uuid: currentUser.uuid, clientId, targetUuid, role });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.clientStaff : []).some(
      (s) => s.client_id === clientId && s.uuid === targetUuid && s.role === (role || '')
    );

    if (!landed) {
      throw new Error("Couldn't confirm the staff assignment was saved — check the connection and try again.");
    }
  };

  // Removes one staff member from a client — admin-only.
  const removeClientStaff = async ({ clientId, targetUuid }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setClientStaff((prev) => prev.filter((s) => !(s.client_id === clientId && s.uuid === targetUuid)));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postRemoveClientStaff({ appsScriptUrl, idToken, uuid: currentUser.uuid, clientId, targetUuid });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.clientStaff : []).some(
      (s) => s.client_id === clientId && s.uuid === targetUuid
    );

    if (stillThere) {
      throw new Error("Couldn't confirm the staff assignment was removed — check the connection and try again.");
    }
  };

  // Creates a new role for a client — admin-only. fields:
  // { title, status, jobPostingTitle, jobPostingLink, jobPostingDescription }
  // — status/job posting fields are optional. Returns the new role_id (the
  // Role form folds criteria editing into the same save, and needs this id
  // to attach them — a role has to exist before it can have criteria).
  const createRole = async ({ clientId, fields }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const entry = {
        role_id: nextRoleId(roles),
        client_id: clientId,
        title: fields.title,
        status: fields.status || 'Open',
        job_posting_title: fields.jobPostingTitle || '',
        job_posting_link: fields.jobPostingLink || '',
        job_posting_description: fields.jobPostingDescription || '',
        created_by: currentUser.uuid,
        created_at: new Date().toISOString(),
      };
      setRoles((prev) => [...prev, entry]);
      return entry.role_id;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateRole({ appsScriptUrl, idToken, uuid: currentUser.uuid, clientId, fields });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.roles : []).find(
      (r) => r.created_by === currentUser.uuid && r.client_id === clientId && r.title === fields.title
    );

    if (!landed) {
      throw new Error("Couldn't confirm the role was saved — check the connection and try again.");
    }
    return landed.role_id;
  };

  // Overwrites an existing role's editable fields — admin-only. client_id
  // is immutable once created.
  const updateRole = async ({ roleId, fields }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setRoles((prev) =>
        prev.map((r) =>
          r.role_id === roleId
            ? {
                ...r,
                title: fields.title,
                status: fields.status || 'Open',
                job_posting_title: fields.jobPostingTitle || '',
                job_posting_link: fields.jobPostingLink || '',
                job_posting_description: fields.jobPostingDescription || '',
              }
            : r
        )
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateRole({ appsScriptUrl, idToken, uuid: currentUser.uuid, roleId, fields });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.roles : []).find((r) => r.role_id === roleId);

    if (!updated || updated.title !== fields.title || updated.status !== (fields.status || 'Open')) {
      throw new Error("Couldn't confirm the role was saved — check the connection and try again.");
    }
  };

  // Permanently removes a role — admin-only. Code.gs cascades its
  // RoleCriteria/Applicants/ApplicantCriteriaAnswers on its side; the local
  // fallback mirrors that here.
  const deleteRole = async ({ roleId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const criteriaIds = roleCriteria.filter((c) => c.role_id === roleId).map((c) => c.criteria_id);
      const applicantIds = applicants.filter((a) => a.role_id === roleId).map((a) => a.applicant_id);
      setRoles((prev) => prev.filter((r) => r.role_id !== roleId));
      setRoleCriteria((prev) => prev.filter((c) => c.role_id !== roleId));
      setApplicants((prev) => prev.filter((a) => a.role_id !== roleId));
      setApplicantAnswers((prev) =>
        prev.filter((ans) => !criteriaIds.includes(ans.criteria_id) && !applicantIds.includes(ans.applicant_id))
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeleteRole({ appsScriptUrl, idToken, uuid: currentUser.uuid, roleId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.roles : []).some((r) => r.role_id === roleId);

    if (stillThere) {
      throw new Error("Couldn't confirm the role was deleted — check the connection and try again.");
    }
  };

  // Replaces a role's full criteria list — admin-only. criteria is always
  // the FULL desired list ([{ criteriaId?, label, sortOrder }]), same
  // full-state shape as updateHolidayList — an item with no criteriaId is a
  // brand-new question (Code.gs mints its id), and any existing criterion
  // left out of the list is deleted along with its answers.
  const updateRoleCriteria = async ({ roleId, criteria }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const keepIds = new Set(criteria.filter((c) => c.criteriaId).map((c) => c.criteriaId));
      const removedIds = roleCriteria
        .filter((c) => c.role_id === roleId && !keepIds.has(c.criteria_id))
        .map((c) => c.criteria_id);

      let nextExisting = roleCriteria.filter((c) => c.role_id !== roleId || keepIds.has(c.criteria_id));
      const minted = [];
      criteria.forEach((item, index) => {
        const sortOrder = item.sortOrder ?? index;
        if (item.criteriaId) {
          nextExisting = nextExisting.map((c) =>
            c.criteria_id === item.criteriaId ? { ...c, label: item.label, sort_order: sortOrder } : c
          );
        } else {
          minted.push({
            criteria_id: nextCriteriaId([...nextExisting, ...minted]),
            role_id: roleId,
            label: item.label,
            sort_order: sortOrder,
            created_by: currentUser.uuid,
            created_at: new Date().toISOString(),
          });
        }
      });

      setRoleCriteria([...nextExisting, ...minted]);
      setApplicantAnswers((prev) => prev.filter((ans) => !removedIds.includes(ans.criteria_id)));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateRoleCriteria({ appsScriptUrl, idToken, uuid: currentUser.uuid, roleId, criteria });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const actual = (fresh ? fresh.roleCriteria : [])
      .filter((c) => c.role_id === roleId)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((c) => ({ label: c.label, sortOrder: c.sort_order }));
    const expected = [...criteria]
      .map((c, index) => ({ label: c.label, sortOrder: c.sortOrder ?? index }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const matches =
      actual.length === expected.length &&
      actual.every((a, i) => a.label === expected[i].label && a.sortOrder === expected[i].sortOrder);

    if (!matches) {
      throw new Error("Couldn't confirm the role's criteria were saved — check the connection and try again.");
    }
  };

  // Creates a new applicant for a role, plus their initial answers —
  // admin-only. fields: { name, availability, cvLink, introVideoLink,
  // testLinks }. answers: { criteriaId: text }. roleId is set once here and
  // never changes — putting the same person forward for a different role
  // means calling this again, not updateApplicant.
  const createApplicant = async ({ roleId, fields, answers }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const applicantId = nextApplicantId(applicants);
      const entry = {
        applicant_id: applicantId,
        role_id: roleId,
        name: fields.name,
        availability: fields.availability || '',
        cv_link: fields.cvLink || '',
        intro_video_link: fields.introVideoLink || '',
        test_links: fields.testLinks || '',
        created_by: currentUser.uuid,
        created_at: new Date().toISOString(),
      };
      setApplicants((prev) => [...prev, entry]);
      setApplicantAnswers((prev) => [
        ...prev,
        ...Object.entries(answers || {})
          .filter(([, text]) => text && text.trim())
          .map(([criteriaId, text]) => ({ applicant_id: applicantId, criteria_id: criteriaId, answer: text.trim() })),
      ]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateApplicant({ appsScriptUrl, idToken, uuid: currentUser.uuid, roleId, fields, answers });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.applicants : []).some(
      (a) => a.created_by === currentUser.uuid && a.role_id === roleId && a.name === fields.name
    );

    if (!landed) {
      throw new Error("Couldn't confirm the applicant was saved — check the connection and try again.");
    }
  };

  // Overwrites an existing applicant's fields and fully replaces their
  // answers — admin-only. answers is always the FULL desired set, same
  // full-replace idea as updateRoleCriteria. roleId can't be changed here.
  const updateApplicant = async ({ applicantId, fields, answers }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setApplicants((prev) =>
        prev.map((a) =>
          a.applicant_id === applicantId
            ? {
                ...a,
                name: fields.name,
                availability: fields.availability || '',
                cv_link: fields.cvLink || '',
                intro_video_link: fields.introVideoLink || '',
                test_links: fields.testLinks || '',
              }
            : a
        )
      );
      setApplicantAnswers((prev) => [
        ...prev.filter((ans) => ans.applicant_id !== applicantId),
        ...Object.entries(answers || {})
          .filter(([, text]) => text && text.trim())
          .map(([criteriaId, text]) => ({ applicant_id: applicantId, criteria_id: criteriaId, answer: text.trim() })),
      ]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateApplicant({ appsScriptUrl, idToken, uuid: currentUser.uuid, applicantId, fields, answers });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.applicants : []).find((a) => a.applicant_id === applicantId);

    if (!updated || updated.name !== fields.name) {
      throw new Error("Couldn't confirm the applicant was saved — check the connection and try again.");
    }
  };

  // Permanently removes an applicant — admin-only. Code.gs also removes
  // their answers on its side; the local fallback mirrors that here.
  const deleteApplicant = async ({ applicantId }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setApplicants((prev) => prev.filter((a) => a.applicant_id !== applicantId));
      setApplicantAnswers((prev) => prev.filter((ans) => ans.applicant_id !== applicantId));
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeleteApplicant({ appsScriptUrl, idToken, uuid: currentUser.uuid, applicantId });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const stillThere = (fresh ? fresh.applicants : []).some((a) => a.applicant_id === applicantId);

    if (stillThere) {
      throw new Error("Couldn't confirm the applicant was deleted — check the connection and try again.");
    }
  };

  // Creates a new employee — admin-only. uuid is generated server-side
  // (Code.gs's nextEmployeeUuid_) to guarantee no collision with a
  // concurrent create; the local-fallback mirrors that scheme client-side.
  const createEmployee = async (fields) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      const entry = {
        uuid: nextEmployeeUuid(users, fields.name),
        name: fields.name,
        role: fields.role || '',
        gmail: fields.gmail,
        contact_number: fields.contactNumber || '',
        timezone: fields.timezone || '',
        date_of_hire: fields.dateOfHire || '',
        birthday: fields.birthday || '',
        address: fields.address || '',
        isAdmin: Boolean(fields.isAdmin),
        holiday_list_id: fields.holidayListId || '',
        status: 'Active',
        resignation_date: '',
      };
      setUsers((prev) => [...prev, entry]);
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postCreateEmployee({ appsScriptUrl, idToken, uuid: currentUser.uuid, fields });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const landed = (fresh ? fresh.users : []).some(
      (u) => u.gmail && u.gmail.toLowerCase() === fields.gmail.toLowerCase() && u.name === fields.name
    );

    if (!landed) {
      throw new Error("Couldn't confirm the employee was saved — check the connection and try again.");
    }
  };

  // Overwrites an existing employee's profile fields — admin-only. Never
  // touches status/resignation_date; see deactivateEmployee/
  // reactivateEmployee for that. Code.gs also blocks this from stripping
  // admin rights from the last remaining active admin — that error surfaces
  // here as a thrown Error same as any other rejection.
  const updateEmployee = async ({ targetUuid, fields }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      let updatedEntry = null;
      setUsers((prev) =>
        prev.map((u) => {
          if (u.uuid !== targetUuid) return u;
          updatedEntry = {
            ...u,
            name: fields.name,
            role: fields.role || '',
            gmail: fields.gmail,
            contact_number: fields.contactNumber || '',
            timezone: fields.timezone || '',
            date_of_hire: fields.dateOfHire || '',
            birthday: fields.birthday || '',
            address: fields.address || '',
            isAdmin: Boolean(fields.isAdmin),
            holiday_list_id: fields.holidayListId || '',
          };
          return updatedEntry;
        })
      );
      // The real backend path re-syncs currentUser from a fresh refetch
      // after every write (see loadData's non-initial-load branch) — mirror
      // that here so editing your own profile doesn't leave currentUser
      // stale until the next reload.
      if (targetUuid === currentUser.uuid && updatedEntry) {
        setCurrentUser(updatedEntry);
      }
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postUpdateEmployee({ appsScriptUrl, idToken, uuid: currentUser.uuid, targetUuid, fields });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.users : []).find((u) => u.uuid === targetUuid);

    if (!updated || updated.name !== fields.name || updated.gmail !== fields.gmail) {
      throw new Error("Couldn't confirm the employee was saved — check the connection and try again.");
    }
  };

  // Soft-deletes an employee — admin-only. Code.gs also blocks this from
  // targeting your own uuid or the last remaining active admin; those
  // errors surface here as a thrown Error same as any other rejection.
  const deactivateEmployee = async ({ targetUuid, resignationDate }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setUsers((prev) =>
        prev.map((u) => (u.uuid === targetUuid ? { ...u, status: 'Inactive', resignation_date: resignationDate } : u))
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postDeactivateEmployee({ appsScriptUrl, idToken, uuid: currentUser.uuid, targetUuid, resignationDate });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.users : []).find((u) => u.uuid === targetUuid);

    if (!updated || updated.status !== 'Inactive') {
      throw new Error("Couldn't confirm the employee was deactivated — check the connection and try again.");
    }
  };

  // Marks an employee Active again and clears resignation_date — admin-only.
  const reactivateEmployee = async ({ targetUuid }) => {
    if (!currentUser) return;
    const appsScriptUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

    if (!appsScriptUrl) {
      setUsers((prev) =>
        prev.map((u) => (u.uuid === targetUuid ? { ...u, status: 'Active', resignation_date: '' } : u))
      );
      return;
    }

    if (!isIdTokenValid) {
      throw new Error('Your session needs a fresh sign-in before this can be saved.');
    }

    await postReactivateEmployee({ appsScriptUrl, idToken, uuid: currentUser.uuid, targetUuid });

    await simulateNetworkDelay(800);
    const fresh = await loadData(false);
    const updated = (fresh ? fresh.users : []).find((u) => u.uuid === targetUuid);

    if (!updated || updated.status !== 'Active') {
      throw new Error("Couldn't confirm the employee was reactivated — check the connection and try again.");
    }
  };

  const getLatestLogFor = (uuid) => {
    const logsForUser = timelogs.filter((l) => l.uuid === uuid);
    if (logsForUser.length === 0) return null;
    return logsForUser.reduce((latest, l) =>
      new Date(l.logtime_utc) > new Date(latest.logtime_utc) ? l : latest
    );
  };

  const value = useMemo(
    () => ({
      users,
      tzOffsets,
      currentUser,
      signInWithGmail,
      signOut,
      timelogs,
      addLog,
      editNote,
      leaves,
      leaveBalances,
      fileLeave,
      decideLeave,
      adjustBalance,
      cancelLeave,
      announcements,
      createAnnouncement,
      updateAnnouncement,
      deleteAnnouncement,
      holidays,
      holidayLists,
      holidayListItems,
      createHoliday,
      updateHoliday,
      deleteHoliday,
      createHolidayList,
      updateHolidayList,
      deleteHolidayList,
      clients,
      clientStaff,
      createClient,
      updateClient,
      deleteClient,
      addClientStaff,
      removeClientStaff,
      roles,
      roleCriteria,
      applicants,
      applicantAnswers,
      createRole,
      updateRole,
      deleteRole,
      updateRoleCriteria,
      createApplicant,
      updateApplicant,
      deleteApplicant,
      createEmployee,
      updateEmployee,
      deactivateEmployee,
      reactivateEmployee,
      getLatestLogFor,
      dataLoaded,
      dataError,
      refetch,
      setIdToken,
      isIdTokenValid,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentUser,
      timelogs,
      users,
      tzOffsets,
      leaves,
      leaveBalances,
      announcements,
      holidays,
      holidayLists,
      holidayListItems,
      clients,
      clientStaff,
      roles,
      roleCriteria,
      applicants,
      applicantAnswers,
      dataLoaded,
      dataError,
      idToken,
      idTokenExpiry,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within an AppProvider');
  return ctx;
}
